/**
 * Centralized Branding Configuration
 *
 * Edit this single file to change ALL branding across the entire dashboard.
 * Colors, logos, and company name are all controlled from here.
 *
 * After editing, the entire app will reflect the new branding automatically.
 */

export interface BrandingConfig {
  company: {
    name: string;
    tagline?: string;
    copyright?: string;
  };
  logos: {
    primary: string;      // Main logo (e.g., "/logo.png")
    wordmark: string;     // Text/wordmark logo (e.g., "/wordmark.svg")
    favicon?: string;     // Optional favicon path
  };
  colors: {
    primary: string;      // Main brand color
    secondary: string;    // Secondary/gradient start color
    tertiary: string;     // Tertiary/gradient end color
    accent: string;       // Accent/highlight color
    ring: string;         // Focus ring color
    gradient: {
      start: string;
      end: string;
      angle: number;      // Gradient angle in degrees
    };
    chart: string[];      // Chart color palette (5+ colors recommended)
    // Page background colors (light and dark mode)
    background?: {
      light: string;      // Background color for light mode (default: #f5f5f5)
      dark: string;       // Background color for dark mode (default: #0a0a0a)
    };
    // Card/panel background colors
    card?: {
      light: string;      // Card background for light mode (default: #ffffff)
      dark: string;       // Card background for dark mode (default: #171717)
    };
  };
  email: {
    headerGradientStart: string;
    headerGradientEnd: string;
    buttonBackground: string;
    accentText: string;
  };
}

/**
 * ============================================
 * EDIT BELOW TO CHANGE BRANDING
 * ============================================
 */
export const branding: BrandingConfig = {
  company: {
    name: "RR Fitness",
    tagline: "Analytics Dashboard",
    copyright: "RR Fitness",
  },

  logos: {
    primary: "/logo.png?v=2",
    wordmark: "/logo.png?v=2",    // Using same logo (no separate wordmark)
    favicon: "/favicon.ico",
  },

  colors: {
    // Main brand colors - Blue theme (Apptics style)
    primary: "#615dff",      // Purple/Blue - primary actions, active states
    secondary: "#11b2f8",    // Light blue/cyan - gradient start
    tertiary: "#504df9",     // Darker blue/purple - gradient end
    accent: "#e8bdff",       // Light purple - highlights, badges
    ring: "#615dff",         // Purple/Blue - focus rings

    // Gradient definition
    gradient: {
      start: "#11b2f8",      // Light blue/cyan
      end: "#504df9",        // Darker blue/purple
      angle: 135,            // Gradient angle (degrees)
    },

    // Chart color palette (balanced for light/dark modes)
    chart: [
      "#09bef5",  // Cyan - primary
      "#615dff",  // Purple/Blue
      "#e8bdff",  // Light purple
      "#11b2f8",  // Light blue
      "#504df9",  // Dark purple
      "#10B981",  // Emerald
      "#3B82F6",  // Blue
      "#8B5CF6",  // Violet
    ],

    // Page background colors
    background: {
      light: "#f5f5f5",   // Light gray for light mode
      dark: "#0a0a0a",    // Near black for dark mode
    },

    // Card/panel background colors
    card: {
      light: "#ffffff",   // White for light mode
      dark: "#171717",    // Dark gray for dark mode
    },
  },

  email: {
    headerGradientStart: "#11b2f8",
    headerGradientEnd: "#504df9",
    buttonBackground: "#615dff",
    accentText: "#615dff",
  },
};

// ============================================
// HELPER FUNCTIONS (do not edit)
// ============================================

/**
 * Generate CSS gradient string from config
 */
export function getGradientCSS(
  gradient: BrandingConfig["colors"]["gradient"] = branding.colors.gradient
): string {
  return `linear-gradient(${gradient.angle}deg, ${gradient.start} 0%, ${gradient.end} 100%)`;
}

/**
 * Generate Tailwind gradient classes
 * Returns: "from-[#11b2f8] to-[#504df9]"
 */
export function getGradientTailwind(
  gradient: BrandingConfig["colors"]["gradient"] = branding.colors.gradient
): string {
  return `from-[${gradient.start}] to-[${gradient.end}]`;
}

/**
 * Get chart color by index (wraps around if index exceeds array length)
 */
export function getChartColor(index: number): string {
  const chart = branding.colors.chart;
  if (chart.length === 0) return branding.colors.primary;
  return chart[index % chart.length] ?? branding.colors.primary;
}

/**
 * Inject all branding CSS variables into the document root
 * Called automatically by ThemeProvider
 */
export function injectBrandingCSS(): void {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  const { colors } = branding;

  // Core brand colors
  root.style.setProperty("--brand-primary", colors.primary);
  root.style.setProperty("--brand-secondary", colors.secondary);
  root.style.setProperty("--brand-tertiary", colors.tertiary);
  root.style.setProperty("--brand-accent", colors.accent);
  root.style.setProperty("--brand-ring", colors.ring);

  // Gradient colors
  root.style.setProperty("--brand-gradient-start", colors.gradient.start);
  root.style.setProperty("--brand-gradient-end", colors.gradient.end);

  // Chart colors (injected after theme detection below)

  // Background colors (with defaults)
  const bgLight = colors.background?.light ?? "#f5f5f5";
  const bgDark = colors.background?.dark ?? "#0a0a0a";
  const cardLight = colors.card?.light ?? "#ffffff";
  const cardDark = colors.card?.dark ?? "#171717";

  root.style.setProperty("--brand-bg-light", bgLight);
  root.style.setProperty("--brand-bg-dark", bgDark);
  root.style.setProperty("--brand-card-light", cardLight);
  root.style.setProperty("--brand-card-dark", cardDark);

  // Also update the semantic color variables
  root.style.setProperty("--primary", colors.primary);
  root.style.setProperty("--ring", colors.ring);

  // Update background based on current theme
  const isDark = root.classList.contains("dark");
  const isBrand = root.classList.contains("brand");

  // Set background and card based on current theme
  if (isBrand) {
    // Brand theme - CSS handles the gradient background via .brand body selector
    // Just clear any inline styles and let globals.css take over
    document.body.style.background = "";
    document.body.style.backgroundColor = "";
    document.body.style.backgroundImage = "";
    // Blue-toned chart palette for brand mode
    const brandChartColors = ["#615dff", "#504df9", "#ffffff", "#11b2f8", "#e8bdff"];
    brandChartColors.forEach((color, i) => {
      root.style.setProperty(`--chart-${i + 1}`, color);
    });
  } else if (isDark) {
    root.style.setProperty("--background", bgDark);
    root.style.setProperty("--card", cardDark);
    document.body.style.background = "";
    document.body.style.backgroundColor = "";
    document.body.style.backgroundImage = "";
    // Blue-toned chart palette for dark mode
    const darkChartColors = ["#615dff", "#504df9", "#ffffff", "#11b2f8", "#e8bdff"];
    darkChartColors.forEach((color, i) => {
      root.style.setProperty(`--chart-${i + 1}`, color);
    });
  } else {
    // Default chart colors from branding config
    colors.chart.forEach((color, i) => {
      root.style.setProperty(`--chart-${i + 1}`, color);
    });
    root.style.setProperty("--background", bgLight);
    root.style.setProperty("--card", cardLight);
    document.body.style.background = "";
    document.body.style.backgroundColor = "";
    document.body.style.backgroundImage = "";
    root.style.removeProperty("--foreground");
    root.style.removeProperty("--card-foreground");
    root.style.removeProperty("--popover");
    root.style.removeProperty("--popover-foreground");
    root.style.removeProperty("--border");
    root.style.removeProperty("--muted-foreground");
  }
}

/**
 * Get the MongoDB collection prefix based on company name
 * Converts "SalesGrid" to "salesgrid_", "My Company" to "my_company_"
 */
export function getCollectionPrefix(): string {
  return branding.company.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") + "_";
}

/**
 * Get a prefixed collection name
 * Example: getCollectionName("users") returns "salesgrid_users"
 */
export function getCollectionName(baseName: string): string {
  return getCollectionPrefix() + baseName;
}

/**
 * Get a prefixed model name (PascalCase)
 * Example: getModelName("User") returns "SalesGrid_User"
 */
export function getModelName(baseName: string): string {
  const prefix = branding.company.name.replace(/[^a-zA-Z0-9]+/g, "");
  return `${prefix}_${baseName}`;
}
